import { useMemo } from 'react';

import type { DomainQueryOptions } from '@/domain/shared/types';
import { useSmoltReportController_getReportDetail } from '@/services/hooks';
import type { SmoltReportDetailResponseDto } from '@/services/types';

import { mapSmoltReportDetailToDomain, type SmoltReportDetailWire } from './mappers';
import type { UseGetReportResult } from './types';

const EMPTY_SMOLT_REPORT_DETAIL: SmoltReportDetailWire = {
    id: 0,
    status: 'finished',
    stockingDate: '',
    lastFeedingDate: undefined,
    deliveryDate: '',
    createdAt: new Date().toISOString(),
    cageId: 0,
    warnings: {
        weightLowFrequency: false,
        weightDataLatency: false,
        dataEndsBeforeDelivery: false,
        weightPeriodTooShort: false,
        welfareLowCoverage: false,
        welfareInsufficientSample: false,
    },
    weightData: {
        distribution: [],
        numMeasurements: 0,
        timeSeries: [],
        conditionFactor: undefined,
        cv: undefined,
        growth: undefined,
        liveWeight: undefined,
        sgr: undefined,
    },
    // While the report loads, the welfare section shows its usual four cards as placeholders.
    includedParameters: ['bodyWounds', 'finDamage', 'gillLid', 'scaleLoss'],
    welfareData: {
        numMeasurements: 0,
        fishWithoutWounds: undefined,
        totalWounds: undefined,
        wounds: { development: [], numMeasurements: 0, acute: undefined, healing: undefined },
        laksvelParameters: [
            { parameter: 'finDamage', development: [], numMeasurements: 0 },
            { parameter: 'gillLid', development: [], numMeasurements: 0 },
            { parameter: 'scaleLoss', development: [], numMeasurements: 0 },
        ],
    },
};

/**
 * Domain hook to fetch a single smolt report detail by cage and report ID.
 *
 * @param cageId - The cage ID
 * @param reportId - The report ID
 * @param options - Optional React Query options (e.g. enabled, refetchInterval)
 * @returns Query result with mapped smolt report detail or undefined
 */
export const useGetReport = (
    cageId: number,
    reportId: number,
    options?: DomainQueryOptions<SmoltReportDetailResponseDto>
): UseGetReportResult => {
    const detailQuery = useSmoltReportController_getReportDetail(cageId, reportId, options);

    const report = useMemo(
        () => mapSmoltReportDetailToDomain(detailQuery.data?.data ?? EMPTY_SMOLT_REPORT_DETAIL),
        [detailQuery.data?.data]
    );

    return {
        ...detailQuery,
        isLoading: detailQuery.isPending,
        data: report,
    };
};
